import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { PictureInPicture2, Send } from "lucide-react";
import { toast } from "sonner";

export const EMOJIS = ["❤️", "😍", "😂", "🥂", "🎉", "👏", "😭", "🔥"];

function useSpeaking(stream) {
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (!stream || !stream.getAudioTracks().length) return undefined;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return undefined;
    const ctx = new Ctx();
    const an = ctx.createAnalyser();
    an.fftSize = 512;
    ctx.createMediaStreamSource(stream).connect(an);
    const buf = new Uint8Array(an.fftSize);
    const t = setInterval(() => {
      an.getByteTimeDomainData(buf);
      let peak = 0;
      for (const v of buf) peak = Math.max(peak, Math.abs(v - 128));
      setOn(peak > 18);
    }, 250);
    return () => { clearInterval(t); ctx.close().catch(() => {}); };
  }, [stream]);
  return on;
}

export async function popOut(v) {
  try { if (document.pictureInPictureElement === v) await document.exitPictureInPicture(); else await v.requestPictureInPicture(); }
  catch { toast.error("Picture in picture isn't available in this browser"); }
}

export function VideoTile({ stream, name, muted, mirror, host, off, raised = false, className = "", style, mark = true, speak = false, onTap = null, tid = "", big = false }) {
  const talking = useSpeaking(speak ? stream : null);
  const lift = raised ? "bottom-[120px]" : "bottom-2";
  const ref = useRef(null);
  useEffect(() => { if (ref.current && ref.current.srcObject !== stream) ref.current.srcObject = stream || null; }, [stream]);
  return (
    <div className={`${className.includes("absolute") ? "" : "relative "}bg-[#111] overflow-hidden rounded-xl transition-shadow duration-200 ${talking ? "ring-[3px] ring-emerald-400" : ""} ${className}`} style={style} data-testid="live-tile" data-tile={tid} data-talking={talking ? "1" : "0"} onClick={onTap || undefined}>
      {stream ? <video ref={ref} autoPlay playsInline muted={muted} className={`w-full h-full object-cover ${mirror ? "-scale-x-100" : ""} ${off ? "opacity-0" : ""}`} />
        : <div className="absolute inset-0 flex items-center justify-center text-white/40 text-xs">Connecting…</div>}
      {off && <div className="absolute inset-0 flex items-center justify-center"><span className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center font-serif text-2xl text-white">{(name || "?")[0]}</span></div>}
      <span className={`absolute left-2 ${lift} text-[11px] bg-black/55 backdrop-blur px-2 py-0.5 rounded-full text-white`} data-testid="live-tile-name">{name}{host ? " · Host" : ""}</span>
      {stream && document.pictureInPictureEnabled && <button onClick={(e) => { e.stopPropagation(); popOut(ref.current); }} className={`absolute right-2 ${big ? "top-[7.5rem]" : "top-2"} z-[2] w-8 h-8 rounded-full bg-black/45 backdrop-blur flex items-center justify-center opacity-70 hover:opacity-100 transition-opacity`} aria-label={`Pop out ${name}`} data-testid={`live-tile-popout-${tid}`}><PictureInPicture2 className="w-4 h-4" /></button>}
      {mark && <img src="/brand/logo-full.png" alt="" className={`absolute right-2 ${lift} w-[18%]`} style={{ minWidth: 56, maxWidth: 120, opacity: 0.8, pointerEvents: "none", userSelect: "none", filter: "drop-shadow(0 1px 2px #0008)" }} data-testid="live-watermark" />}
    </div>
  );
}

export function FloatingReactions({ room }) {
  const [items, setItems] = useState([]);
  useEffect(() => room?.on("react", (m) => {
    const id = Math.random().toString(36).slice(2);
    setItems((x) => [...x.slice(-30), { id, emoji: m.emoji, x: Math.random() * 40 - 20 }]);
    setTimeout(() => setItems((x) => x.filter((i) => i.id !== id)), 2600);
  }), [room]);
  return (
    <div className="absolute right-16 bottom-28 w-16 h-[60%] pointer-events-none z-20" data-testid="live-reactions">
      <AnimatePresence>{items.map((i) => (
        <motion.span key={i.id} className="absolute bottom-0 left-1/2 text-3xl" initial={{ y: 0, x: i.x, opacity: 0, scale: 0.6 }} animate={{ y: -320, x: i.x * 2, opacity: [0, 1, 1, 0], scale: [0.6, 1.2, 1] }} transition={{ duration: 2.5, ease: "easeOut" }}>{i.emoji}</motion.span>
      ))}</AnimatePresence>
    </div>
  );
}

export function OverlayChat({ room, canChat = true, lift = 0 }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  useEffect(() => room?.on("chat", (m) => setMsgs((x) => [...x.slice(-40), m])), [room]);
  function send(e) { e.preventDefault(); if (text.trim()) { room.chat(text.trim()); setText(""); } }
  return (
    <div className="absolute left-0 right-0 bottom-0 z-20 p-3 pt-16 bg-gradient-to-t from-black/75 via-black/30 to-transparent transition-[bottom] duration-300" style={{ bottom: lift }} data-testid="live-overlay-chat">
      <div className="max-w-sm space-y-1.5 mb-3 max-h-48 overflow-hidden flex flex-col justify-end [mask-image:linear-gradient(to_bottom,transparent,black_35%)]">
        <AnimatePresence initial={false}>{msgs.slice(-7).map((m) => (
          <motion.p key={m.id} layout initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} className="text-sm text-white leading-snug drop-shadow" data-testid="live-chat-msg">
            <b className={m.system ? "text-amber-300" : "text-[var(--gold)]"}>{m.name}</b> <span className="text-white/90">{m.text}</span>
          </motion.p>
        ))}</AnimatePresence>
      </div>
      <div className="flex items-center gap-2">
        {canChat && <form onSubmit={send} className="flex-1 flex gap-1.5 min-w-0">
          <input value={text} onChange={(e) => setText(e.target.value)} maxLength={200} placeholder="Say something nice…" className="flex-1 min-w-0 bg-white/15 backdrop-blur border border-white/20 rounded-full px-4 py-2 text-sm text-white placeholder:text-white/60 outline-none focus:border-[var(--gold)]" data-testid="live-chat-input" />
          <button className="w-9 h-9 shrink-0 rounded-full bg-[var(--gold)] text-black flex items-center justify-center" aria-label="Send" data-testid="live-chat-send"><Send className="w-4 h-4" /></button>
        </form>}
        <div className="flex gap-0.5 shrink-0" data-testid="live-emoji-bar">
          {EMOJIS.slice(0, 5).map((e, i) => <button key={e} onClick={() => room.react(e)} className={`${i > 2 ? "hidden sm:block" : ""} text-xl w-8 h-8 rounded-full hover:bg-white/15 active:scale-125 transition-transform`} aria-label={`React ${e}`} data-testid={`live-react-${e}`}>{e}</button>)}
        </div>
      </div>
    </div>
  );
}
